import * as Crypto from 'expo-crypto'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { storage } from '@/lib/storage'
import { useAnalytics } from '../hooks/useAnalytics'

export const SESSION_ID_KEY = 'turbina_onboarding_session_id'
export const DATA_KEY = 'turbina_onboarding_data'

// Map frontend camelCase option keys to snake_case PostgreSQL enum labels.
export const GOAL_MAP: Record<string, string> = {
  studyEnglish: 'study_english',
  study: 'study',
  work: 'work',
  experience: 'experience',
  notSure: 'not_sure',
}

export const DESTINATION_MAP: Record<string, string> = {
  brisbane: 'brisbane',
  sydney: 'sydney',
  melbourne: 'melbourne',
  perth: 'perth',
  adelaide: 'adelaide',
  regional: 'regional',
  anotherCountry: 'another_country',
  notSure: 'not_sure',
}

export const TIMELINE_MAP: Record<string, string> = {
  ready_to_go: '4to6',
  almost_ready: '6to8',
  still_planning: '8to12',
  just_exploring: '12to18',
  // legacy aliases for cached onboarding keys from older app versions.
  '4to6': '4to6',
  '6to8': '6to8',
  '8to12': '8to12',
  '12to18': '12to18',
}

export const AGE_MAP: Record<string, string> = {
  under18: 'under18',
  '18to24': '18to24',
  '25to34': '25to34',
  '35to44': '35to44',
  '45to54': '45to54',
  '55to64': '55to64',
  '65plus': '65plus',
  // Legacy aliases for cached onboarding keys from older app versions.
  '18to21': '18to24',
  '22to25': '18to24',
  '26to30': '25to34',
  '30plus': '25to34',
}

export const SAVINGS_MAP: Record<string, string> = {
  noProblem: 'no_problem',
  someSavings: 'some_savings',
  noSavings: 'no_savings',
}

export const SITUATION_MAP: Record<string, string> = {
  highSchool: 'high_school',
  university: 'university',
  working: 'working',
  student: 'student',
  other: 'other',
}

export function toEnum(map: Record<string, string>, value: string): string {
  return map[value] ?? value
}

const generateUUID = () => Crypto.randomUUID()

interface OnboardingState {
  sessionId: string | null
  isLoaded: boolean
  goal: string | null
  destination: string | null
  timeline: string | null
  originCity: string | null
  originCountry: string | null
  originCountryCode: string | null
  age: string | null
  savings: string | null
  situation: string | null
  setGoal: (v: string) => void
  setDestination: (v: string) => void
  setTimeline: (v: string) => void
  setOrigin: (city: string, country: string, countryCode: string) => void
  setAge: (v: string) => void
  setSavings: (v: string) => void
  setSituation: (v: string) => void
}

const OnboardingContext = createContext<OnboardingState | null>(null)

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { capture } = useAnalytics()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const sessionIdRef = useRef<string | null>(null)

  const [isLoaded, setIsLoaded] = useState(false)
  const [goal, setGoalState] = useState<string | null>(null)
  const [destination, setDestinationState] = useState<string | null>(null)
  const [timeline, setTimelineState] = useState<string | null>(null)
  const [originCity, setOriginCityState] = useState<string | null>(null)
  const [originCountry, setOriginCountryState] = useState<string | null>(null)
  const [originCountryCode, setOriginCountryCodeState] = useState<string | null>(null)
  const [age, setAgeState] = useState<string | null>(null)
  const [savings, setSavingsState] = useState<string | null>(null)
  const [situation, setSituationState] = useState<string | null>(null)

  const dataRef = useRef<{
    goal: string | null
    destination: string | null
    timeline: string | null
    originCity: string | null
    originCountry: string | null
    originCountryCode: string | null
    age: string | null
    savings: string | null
    situation: string | null
  }>({
    goal: null,
    destination: null,
    timeline: null,
    originCity: null,
    originCountry: null,
    originCountryCode: null,
    age: null,
    savings: null,
    situation: null,
  })

  function applySavedData(saved: typeof dataRef.current) {
    dataRef.current = saved
    if (saved.goal) setGoalState(saved.goal)
    if (saved.destination) setDestinationState(saved.destination)
    if (saved.timeline) setTimelineState(saved.timeline)
    if (saved.originCity) setOriginCityState(saved.originCity)
    if (saved.originCountry) setOriginCountryState(saved.originCountry)
    if (saved.originCountryCode) setOriginCountryCodeState(saved.originCountryCode)
    if (saved.age) setAgeState(saved.age)
    if (saved.savings) setSavingsState(saved.savings)
    if (saved.situation) setSituationState(saved.situation)
  }

  useEffect(() => {
    const initSession = async () => {
      let id = await storage.get(SESSION_ID_KEY)
      if (!id) {
        id = generateUUID()
        await storage.set(SESSION_ID_KEY, id)
        capture('onboarding_started', { session_id: id })
      }
      sessionIdRef.current = id
      setSessionId(id)

      const raw = await storage.get(DATA_KEY)
      if (raw) {
        try {
          applySavedData(JSON.parse(raw) as typeof dataRef.current)
        } catch (err) {
          console.warn('[onboarding] corrupted cache payload, clearing local data', err)
          await storage.remove(DATA_KEY)
        }
      }

      setIsLoaded(true)
    }

    initSession().catch((err) => console.warn('[onboarding] init failed', err))
  }, [capture])

  const saveData = useCallback(() => {
    storage
      .set(DATA_KEY, JSON.stringify(dataRef.current))
      .catch((err) => console.warn('[onboarding] save failed', err))
  }, [])

  const setGoal = useCallback(
    (v: string) => {
      setGoalState(v)
      dataRef.current = { ...dataRef.current, goal: v }
      saveData()
    },
    [saveData]
  )

  const setDestination = useCallback(
    (v: string) => {
      setDestinationState(v)
      dataRef.current = { ...dataRef.current, destination: v }
      saveData()
    },
    [saveData]
  )

  const setTimeline = useCallback(
    (v: string) => {
      setTimelineState(v)
      dataRef.current = { ...dataRef.current, timeline: v }
      saveData()
    },
    [saveData]
  )

  const setOrigin = useCallback(
    (city: string, country: string, countryCode: string) => {
      setOriginCityState(city)
      setOriginCountryState(country)
      setOriginCountryCodeState(countryCode)
      dataRef.current = {
        ...dataRef.current,
        originCity: city,
        originCountry: country,
        originCountryCode: countryCode,
      }
      saveData()
    },
    [saveData]
  )

  const setAge = useCallback(
    (v: string) => {
      setAgeState(v)
      dataRef.current = { ...dataRef.current, age: v }
      saveData()
    },
    [saveData]
  )

  const setSavings = useCallback(
    (v: string) => {
      setSavingsState(v)
      dataRef.current = { ...dataRef.current, savings: v }
      saveData()
    },
    [saveData]
  )

  const setSituation = useCallback(
    (v: string) => {
      setSituationState(v)
      dataRef.current = { ...dataRef.current, situation: v }
      saveData()
    },
    [saveData]
  )

  return (
    <OnboardingContext.Provider
      value={{
        sessionId,
        isLoaded,
        goal,
        destination,
        timeline,
        originCity,
        originCountry,
        originCountryCode,
        age,
        savings,
        situation,
        setGoal,
        setDestination,
        setTimeline,
        setOrigin,
        setAge,
        setSavings,
        setSituation,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding(): OnboardingState {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider')
  return ctx
}

export function useOnboardingOptional(): OnboardingState | null {
  return useContext(OnboardingContext)
}
